import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';

import '@cosmos/modal';

type ModalElement = HTMLDivElement & {
  openModal: () => void;
  closeModal: () => void;
};

export type ModalProps = {
  children: React.ReactNode;
  show: boolean;
  className?: string;
  onClose: () => void;
  centered?: boolean;
  docked?: boolean;
  onOpen?: () => void;
};

let modalRoot = document.getElementById('modal');
if (!modalRoot) {
  modalRoot = document.createElement('div');
  modalRoot.setAttribute('id', 'modal');
  document.body.appendChild(modalRoot);
}

const Modal = ({
  children,
  show,
  onClose,
  onOpen,
  centered = true,
  docked = false,
  className = ''
}: ModalProps) => {
  const modalRef = useRef<ModalElement>(null);

  useEffect(() => {
    if (show) {
      modalRef.current?.openModal();

      return;
    }

    modalRef.current?.closeModal();
  }, [show]);

  useEffect(() => {
    const current = modalRef.current;
    current?.addEventListener('close', onClose);
    onOpen && current?.addEventListener('open', onOpen);

    return () => {
      current?.removeEventListener('close', onClose);
      onOpen && current?.removeEventListener('open', onOpen);
    };
  }, [onOpen, onClose]);

  const modalClasses = clsx({ 'hot-dropdocked': docked }, className);

  if (!show) return null;

  return createPortal(
    <hot-modal ref={modalRef} class={modalClasses} {...(centered ? { position: 'centered' } : {})}>
      {children}
    </hot-modal>,
    modalRoot as HTMLElement
  );
};

export default Modal;
